import { languageService } from '@unpispas/upp-base';

import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { PriceZone } from '../objects/pricezone';

import type { PlaceView } from './place';
import type { QrCodeView } from './qrcode';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _PriceZoneView extends ViewObject<PriceZone> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get pricezone(): PriceZone {
        return this.object;
    }

    constructor(object: PriceZone, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _validtables: QrCodeView[] | null = null;

    private RefreshView(targets: string[]): void {
        if (targets.includes('QRCODE') || targets.includes('PRICEZONE')){
            this._validtables = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void {
        const weakref = new WeakRef(this);

        for (const _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        this._subscriptions.push(
            this.pricezone.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(targets);
                    }
                },
            ),
        );

        const _place = this.pricezone.place;
        if (_place){
            this._subscriptions.push(
                _place.OnRefresh.subscribe(
                    (targets: string[]) => {
                        const self = weakref.deref();
                        if (self){
                            self.RefreshView(targets);
                        }
                    },
                ),
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/

    get place(): PlaceView | null {
        return this.alives.get(this.pricezone.place) as PlaceView | null;
    }

    /** Display label for lists/selectors: translated name, or default price-zone i18n key. */
    get Label(): string {
        const _tr = this.pricezone.name?.tr() || null;
        if (_tr){
            return _tr;
        }

        const _lang = this.data.injector.get(languageService);
        return _lang.tr('@place_table_price_zone_default');
    }

    /** Active tables assigned to this price zone as canonical QrCode views. */
    get tables(): QrCodeView[] {
        if (this._validtables === null){
            const _tables: QrCodeView[] = [];

            for (const _qrcode of this.pricezone.qrcodes){
                const _view = this.alives.get(_qrcode) as QrCodeView | null;
                if (_view){
                    _tables.push(_view);
                }
            }

            this._validtables = _tables;
        }

        return this._validtables;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type PriceZoneView = ViewType<_PriceZoneView, PriceZone>;

export function PriceZoneViewProxy(object: PriceZone, data: dataService): PriceZoneView {
    return new _PriceZoneView(object, data).Proxy as PriceZoneView;
}
